/** Query hooks of the Types pages (deliverable 2): reads under `['types']`, one mutation per write. */
import {
  type ConvertFieldRequestV1,
  docsTitles,
  type FieldCreateRequestV1,
  type FieldUpdateRequestV1,
  fieldsArchive,
  fieldsConvert,
  fieldsConvertPreview,
  fieldsCreate,
  fieldsRestore,
  fieldsUpdate,
  linksTypes,
  type TypeCreateRequestV1,
  type TypeUpdateRequestV1,
  typesArchive,
  typesCreate,
  typesList,
  typesRestore,
  typesUpdate,
} from '@codex/contracts';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../auth/client.js';

export const typesKey = ['types'] as const;
export const linkTypesKey = ['link-types'] as const;
/** The first parts of every page's `docFieldsKey` and `docQuarantineKey`. */
export const docFieldsPrefix = ['doc-fields'] as const;
export const docQuarantinePrefix = ['doc-quarantine'] as const;

export function useTypes() {
  return useQuery({
    queryKey: typesKey,
    queryFn: async () => (await api(typesList)).items,
    retry: false,
  });
}

export function useLinkTypes() {
  return useQuery({
    queryKey: linkTypesKey,
    queryFn: async () => (await api(linksTypes)).items,
    retry: false,
  });
}

/** A write that refreshes the types when it succeeds. */
function useWrite<V, R>(write: (variables: V) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: () => client.invalidateQueries({ queryKey: typesKey }),
  });
}

interface Id {
  readonly id: string;
}

export const useCreateType = () =>
  useWrite((body: TypeCreateRequestV1) => api(typesCreate, { body }));
export const useUpdateType = () =>
  useWrite(({ id, body }: Id & { body: TypeUpdateRequestV1 }) =>
    api(typesUpdate, { params: { id }, body }),
  );
export const useArchiveType = () => useWrite(({ id }: Id) => api(typesArchive, { params: { id } }));
export const useRestoreType = () => useWrite(({ id }: Id) => api(typesRestore, { params: { id } }));

export const useCreateField = () =>
  useWrite((body: FieldCreateRequestV1) => api(fieldsCreate, { body }));
export const useUpdateField = () =>
  useWrite(({ id, body }: Id & { body: FieldUpdateRequestV1 }) =>
    api(fieldsUpdate, { params: { id }, body }),
  );
export const useArchiveField = () =>
  useWrite(({ id }: Id) => api(fieldsArchive, { params: { id } }));
export const useRestoreField = () =>
  useWrite(({ id }: Id) => api(fieldsRestore, { params: { id } }));
/** The conversion as mutation options, so a spec can run its success path without React. */
export function convertFieldOptions(client: QueryClient) {
  return {
    mutationFn: ({ id, body }: Id & { body: ConvertFieldRequestV1 }) =>
      api(fieldsConvert, { params: { id }, body }),
    // A conversion rewrites the props and the set-aside list of every page of the type, so each
    // page's copies go stale too. The prefixes are those of `docFieldsKey` and `docQuarantineKey`
    // (pills/doc-fields.ts, which imports this module, so they are spelled out here).
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: typesKey }),
        client.invalidateQueries({ queryKey: docFieldsPrefix }),
        client.invalidateQueries({ queryKey: docQuarantinePrefix }),
      ]),
  };
}
export const useConvertField = () => useMutation(convertFieldOptions(useQueryClient()));

/** The preview writes nothing, so it refreshes nothing. */
export function useConvertPreview() {
  return useMutation({
    mutationFn: ({ id, body }: Id & { body: ConvertFieldRequestV1 }) =>
      api(fieldsConvertPreview, { params: { id }, body }),
  });
}

/** Titles for a conversion's samples, in requests of at most 500 ids. */
export async function fetchTitles(ids: readonly string[]): Promise<Map<string, string>> {
  const titles = new Map<string, string>();
  for (let start = 0; start < ids.length; start += 500) {
    const answer = await api(docsTitles, { body: { ids: ids.slice(start, start + 500) } });
    for (const item of answer.titles) if (item.title !== null) titles.set(item.id, item.title);
  }
  return titles;
}
